import { forwardRef, type FormEvent } from 'react'
import {
  AiSearchInput as AiSearch,
  type AiSearchInputRef,
} from './ai-search-input/ai-search-input'
import { getDefaultSearchPlaceholders } from '../constants/default-search-placeholders'
import { queryAiSearch } from '../services/query-ai-search'
import {
  aiSearchInputFocusTrackingEvent,
  aiSearchInputSubmitTrackingEvent,
} from '../tracking'
import { useAnalytics } from '@cinch-nx/shared-analytics'

type AiSearchInputProps = {
  placeholders?: string[]
  showSubmitButton?: boolean
  onFocus?: (event: FormEvent<HTMLInputElement>) => void
}

export const AiSearchInput = forwardRef<AiSearchInputRef, AiSearchInputProps>(
  (props: AiSearchInputProps, ref) => {
    const { trackEvent } = useAnalytics()

    const onSearchHandler = async (message: string) => {
      const result = await queryAiSearch(message)
      if (result) {
        const event = aiSearchInputSubmitTrackingEvent(message, result)
        trackEvent(event)
      }
      const fallback = '/used-cars'
      if (result?.content?.searchUrl) {
        const resolvedUrl = new URL(
          result.content.searchUrl,
          window.location.origin,
        )
        // strip cross-origin redirects — keep only path + search + hash
        window.location.href =
          resolvedUrl.pathname + resolvedUrl.search + resolvedUrl.hash
      } else {
        window.location.href = fallback
      }
    }

    const onFocusCallback = (event: FormEvent<HTMLInputElement>) => {
      trackEvent(aiSearchInputFocusTrackingEvent)
      props.onFocus?.(event)
    }
    return (
      <AiSearch
        ref={ref}
        aria-label={'AI powered search input'}
        placeholders={props.placeholders ?? getDefaultSearchPlaceholders()}
        showSubmitButton={props.showSubmitButton}
        onFocus={onFocusCallback}
        onSearch={onSearchHandler}
      />
    )
  },
)

AiSearchInput.displayName = 'AiSearchInput'
